<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { api } from '../../services/api'

interface Dashboard {
  bottles: Record<string, number>
  quotes: Record<string, number>
  activity: Record<string, number>
  recent: Array<{
    id: string
    bottleId: string
    bottleName: string
    type: string
    occurredAt: string
  }>
}
const data = ref<Dashboard | null>(null)
const error = ref('')
onMounted(async () => {
  try {
    data.value = await api('/admin/dashboard')
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Dashboard failed to load.'
  }
})
</script>

<template>
  <header class="admin-heading">
    <div>
      <p class="eyebrow">Project compass</p>
      <h1>Overview</h1>
      <p>Watch every message move, without tracking the people carrying it.</p>
    </div>
    <div class="date-chip">
      {{ new Date().toLocaleDateString(undefined, { dateStyle: 'long' }) }}
    </div>
  </header>
  <p v-if="error" class="form-error banner">{{ error }}</p>
  <template v-if="data">
    <section class="metric-grid">
      <article>
        <span>Total bottles</span><strong>{{ data.bottles.total ?? 0 }}</strong
        ><small>{{ data.bottles.hidden ?? 0 }} currently hidden</small>
      </article>
      <article>
        <span>Waiting with finders</span><strong>{{ data.bottles.found ?? 0 }}</strong
        ><small>Temporary active handoffs</small>
      </article>
      <article>
        <span>Traveller notes</span><strong>{{ data.activity.comments ?? 0 }}</strong
        ><small>Anonymous messages</small>
      </article>
      <article>
        <span>Open reports</span><strong>{{ data.activity.open_reports ?? 0 }}</strong
        ><small><RouterLink to="/admin/reports">Review reports →</RouterLink></small>
      </article>
    </section>
    <div class="admin-grid">
      <section class="admin-card">
        <div class="card-heading">
          <div>
            <p class="eyebrow">Live trail</p>
            <h2>Recent movements</h2>
          </div>
          <RouterLink to="/admin/bottles">All bottles →</RouterLink>
        </div>
        <ol class="activity-list">
          <li v-for="item in data.recent" :key="item.id">
            <span class="activity-icon">◇</span>
            <div>
              <strong>{{ item.bottleName }}</strong>
              <p>{{ item.type.replaceAll('_', ' ') }}</p>
            </div>
            <time>{{ new Date(item.occurredAt).toLocaleDateString() }}</time>
          </li>
        </ol>
      </section>
      <section class="admin-card">
        <div class="card-heading">
          <div>
            <p class="eyebrow">At a glance</p>
            <h2>Journey totals</h2>
          </div>
        </div>
        <dl class="summary-list">
          <div>
            <dt>Finds</dt>
            <dd>{{ data.activity.finds ?? 0 }}</dd>
          </div>
          <div>
            <dt>Re-hides</dt>
            <dd>{{ data.activity.rehides ?? 0 }}</dd>
          </div>
          <div>
            <dt>Active quotes</dt>
            <dd>{{ data.quotes.active ?? 0 }}</dd>
          </div>
          <div>
            <dt>Missing</dt>
            <dd>{{ data.bottles.missing ?? 0 }}</dd>
          </div>
        </dl>
      </section>
    </div>
  </template>
</template>
